"use client";

import { useEffect, useRef, useState } from "react";
import QosmCode from "./QosmCode";
import { accent } from "@/lib/palette";

const pillars = [
  {
    title: "Designed for LLMs",
    desc: "Typed LLM calls are a language primitive. model takes a prompt, returns typed structured data. Minimal syntax, no implicit behavior \u2014 LLMs read it and write it natively.",
    code: `let classify text =
  model @{category: String, confidence: Float}
  \`Classify: {{ text }}\`;

// model is a keyword. It returns typed structured data.
// No SDK, no parsing \u2014 just call model with a type.`,
    tags: ["Typed LLM calls", "Minimal syntax", "LLM-readable"],
  },
  {
    title: "Types work for you, not against you",
    desc: "You never write type annotations \u2014 the compiler infers everything. Parse JSON into typed records with a single call. Flexible records adapt to any schema.",
    code: `let process data =
  let parsed = parse_json @{name: String, score: Float} data;
  match parsed with
  | Ok r -> .name r
  | Err e -> "parse failed";

// No type annotations anywhere. Fully inferred.
// parse_json: one call, typed boundary crossing.`,
    tags: ["Full type inference", "Typed JSON parsing", "Flexible records"],
  },
  {
    title: "No invisible runtime errors",
    desc: "Every computation that can fail returns a Result. No exceptions, no null, no undefined. If it compiles, every failure path is explicitly handled in the code.",
    code: `// model returns Result<'a, String>, not a bare value.
// parse_json returns Result<'a, ParseError>.
// The compiler forces you to handle both cases.

let safe_classify text =
  match model @{label: String} \`Label: {{ text }}\` with
  | Ok r -> .label r
  | Err e -> "unknown";`,
    tags: ["Result types", "No exceptions", "No null / undefined"],
  },
];

const stackLayers = [
  {
    title: "Interpreter",
    desc: "Tree-walking evaluator for development and debugging. Instant feedback, full error traces.",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="4 17 10 11 4 5" />
        <line x1="12" y1="19" x2="20" y2="19" />
      </svg>
    ),
  },
  {
    title: "Bytecode VM",
    desc: "Stack-based virtual machine. Compiles to bytecode for fast, sandboxed execution in production.",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <rect x="9" y="9" width="6" height="6" />
        <line x1="9" y1="2" x2="9" y2="4" />
        <line x1="15" y1="2" x2="15" y2="4" />
        <line x1="9" y1="20" x2="9" y2="22" />
        <line x1="15" y1="20" x2="15" y2="22" />
        <line x1="20" y1="9" x2="22" y2="9" />
        <line x1="20" y1="15" x2="22" y2="15" />
        <line x1="2" y1="9" x2="4" y2="9" />
        <line x1="2" y1="15" x2="4" y2="15" />
      </svg>
    ),
  },
  {
    title: "JavaScript Codegen",
    desc: "Compile to ESTree for embedding in any JavaScript runtime. Same semantics, native performance.",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    ),
  },
  {
    title: "Unikernel",
    desc: "The security endgame. A minimal, single-purpose VM image with no OS surface. Maximum isolation for production agent deployment.",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    comingSoon: true,
  },
];

function ScrollReveal({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-500 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function StackVertical() {
  return (
    <section id="stack" className="py-24 px-4 max-md:py-16">
      <div className="max-w-[900px] mx-auto">
        <div className="text-center mb-16 max-md:mb-10">
          <h2 className="text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-text-primary tracking-tight mb-4">
            A full stack, built for <span style={{ color: "#f9a8d4" }}>agents</span>.
          </h2>
          <p className="text-lg text-text-secondary max-w-[600px] mx-auto leading-relaxed max-md:text-base">
            Not adapted from an existing language. Designed from scratch so LLMs can read it, write it, and never produce unsafe code.
          </p>
        </div>

        {/* Language pillars */}
        <div className="flex flex-col gap-6 mb-16 max-md:mb-10">
          {pillars.map((pillar, i) => (
            <ScrollReveal key={pillar.title} delay={i * 150}>
              <div className="rounded-xl border border-border overflow-hidden">
                <div className="px-6 py-5 bg-bg-elevated max-md:px-4 max-md:py-4">
                  <h3
                    className="text-xs font-semibold uppercase tracking-[0.2em] mb-2"
                    style={{ color: accent.DEFAULT }}
                  >
                    {pillar.title}
                  </h3>
                  <p className="text-sm text-text-secondary leading-relaxed mb-3">
                    {pillar.desc}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {pillar.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-[0.65rem] font-mono font-medium px-2 py-0.5 bg-bg-surface text-text-muted rounded whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="border-t border-border bg-bg-inset">
                  <QosmCode
                    code={pillar.code}
                    className="m-0 px-6 pt-4 pb-4 text-[13px] leading-relaxed overflow-x-auto max-md:text-sm max-md:px-4"
                  />
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>

        {/* Execution stack */}
        <ScrollReveal delay={450}>
          <div className="text-center mb-8">
            <h3 className="text-lg font-semibold text-text-primary mb-2">
              Four execution targets. One semantics.
            </h3>
            <p className="text-sm text-text-muted max-w-[500px] mx-auto">
              Write once, run on any backend. From REPL prototyping to hardened production deployment.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-4 gap-3 max-md:grid-cols-2">
          {stackLayers.map((layer, i) => (
            <ScrollReveal key={layer.title} delay={500 + i * 100}>
              <div className={`rounded-xl border bg-bg-elevated px-4 py-4 flex flex-col h-full ${layer.comingSoon ? "border-border border-dashed" : "border-border"}`}>
                <div className="flex items-center gap-2 mb-2.5">
                  <span
                    className={layer.comingSoon ? "text-text-dim" : ""}
                    style={layer.comingSoon ? undefined : { color: accent.DEFAULT }}
                  >
                    {layer.icon}
                  </span>
                  <span className={`text-xs font-semibold ${layer.comingSoon ? "text-text-dim" : "text-text-primary"}`}>
                    {layer.title}
                  </span>
                  {layer.comingSoon && (
                    <span className="text-[8px] font-semibold uppercase tracking-widest text-text-dim ml-auto">
                      Soon
                    </span>
                  )}
                </div>
                <p className={`text-[11px] leading-relaxed ${layer.comingSoon ? "text-text-dim" : "text-text-muted"}`}>
                  {layer.desc}
                </p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}
